// The round screen (and any other non-monitor screen surface): a static black display with faint noise, a
// light grey letter in the middle and dark interlace scanlines (user request 2026-10-23). The letter is the manifest
// screen's `letter` (default "A": the listening room; the concrete room's screen shows "B"). Drawn once per letter into a small canvas texture: no video,
// no animation, no per-frame cost. The class keeps the old video API (set/play/pause/sync) as no-ops so the
// media code that drives it doesn't change.

import { CanvasTexture, Color, LinearFilter, Mesh, MeshBasicMaterial, SRGBColorSpace, type Material } from 'three';
import { uvOrientation } from './posters';

const SIZE = 256;

function drawFace(letter: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = SIZE;
  const g = c.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, SIZE, SIZE);
  // faint static: sparse grey pixels on black, generated once
  const img = g.getImageData(0, 0, SIZE, SIZE);
  const d = img.data;
  let seed = 7;
  for (let i = 0; i < d.length; i += 4) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const v = (seed >>> 24) * 0.09; // 0..23: dark grain
    d[i] = d[i + 1] = d[i + 2] = v;
  }
  g.putImageData(img, 0, 0);
  g.fillStyle = '#c8c8c8'; // a little dimmer than pure white
  g.font = `600 ${Math.round(SIZE * 0.5)}px ui-monospace, "SF Mono", Menlo, monospace`;
  // centre the glyph's visible box (not the line box) on the disc, for any letter
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  const m = g.measureText(letter);
  const w = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
  const h = m.actualBoundingBoxAscent + m.actualBoundingBoxDescent;
  g.fillText(letter, (SIZE - w) / 2 + m.actualBoundingBoxLeft, (SIZE - h) / 2 + m.actualBoundingBoxAscent);
  // interlace: every other pair of scanlines darkened, like an old CRT
  g.fillStyle = 'rgba(0,0,0,0.55)';
  for (let y = 0; y < SIZE; y += 4) g.fillRect(0, y, SIZE, 2);
  return c;
}

export class ScreenVideo {
  /** one face texture per letter */
  private textures = new Map<string, CanvasTexture>();
  private originals = new Map<Mesh, Material | Material[]>();
  private mats: MeshBasicMaterial[] = [];
  url: string | null = null;

  constructor(
    private meshes: Mesh[],
    private invalidate: () => void,
    _isVisible: () => boolean = () => true,
    /** the letter a screen mesh shows (manifest screens[].letter) */
    private letterOf: (mesh: Mesh) => string = () => 'A',
  ) {
    for (const mesh of meshes) this.applyTo(mesh);
    this.invalidate();
  }

  private faceTexture(letter: string): CanvasTexture {
    let tex = this.textures.get(letter);
    if (!tex) {
      tex = new CanvasTexture(drawFace(letter));
      tex.colorSpace = SRGBColorSpace;
      tex.flipY = false;
      tex.generateMipmaps = false;
      tex.minFilter = LinearFilter;
      this.textures.set(letter, tex);
    }
    return tex;
  }

  get active(): boolean { return false; }
  get playing(): boolean { return false; }
  get textureBytes(): number { return this.meshes.length ? new Set(this.meshes.map((m) => this.letterOf(m))).size * SIZE * SIZE * 4 : 0; }
  get needsContinuousRender(): boolean { return false; }

  /** The screen is static: media items no longer drive it. */
  set(url: string | null): void { this.url = url; }
  play(): void {}
  pause(): void {}
  sync(_position: number, _playing: boolean): void {}

  private applyTo(mesh: Mesh): void {
    this.originals.set(mesh, mesh.material);
    const old = mesh.material as MeshBasicMaterial;
    const m = old.clone() as MeshBasicMaterial;
    m.onBeforeCompile = old.onBeforeCompile;
    m.customProgramCacheKey = old.customProgramCacheKey;
    m.userData = old.userData;
    const tex = this.faceTexture(this.letterOf(mesh) || 'A').clone();
    const o = uvOrientation(mesh);
    if (o.flipU) { tex.repeat.x = -1; tex.offset.x = 1; }
    if (o.flipV) { tex.repeat.y = -1; tex.offset.y = 1; }
    tex.needsUpdate = true;
    m.map = tex;
    m.vertexColors = false;
    m.color = new Color(1, 1, 1);
    m.toneMapped = false;
    m.needsUpdate = true;
    mesh.material = m;
    this.mats.push(m);
  }

  /** Move to other screen meshes (the scene was replaced by another tier). */
  setMeshes(meshes: Mesh[]): void {
    for (const [mesh, mat] of this.originals) mesh.material = mat;
    this.originals.clear();
    for (const m of this.mats) { m.map?.dispose(); m.dispose(); }
    this.mats = [];
    this.meshes = meshes;
    for (const mesh of meshes) this.applyTo(mesh);
    this.invalidate();
  }
}
